import React, { useState, useContext } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress
} from '@mui/material'
import useOrders, { Order } from 'hooks/useOrder'
import { PRODUCT_IMAGE_BASE_URL } from 'config'
import { formatDisplayDate, formatToTitleCase } from 'utils/formatUtils'
import DownloadIcon from '@mui/icons-material/Download'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import PersonIcon from '@mui/icons-material/Person'
import ReceiptIcon from '@mui/icons-material/Receipt'
import ReplayIcon from '@mui/icons-material/Replay'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { CartContext } from 'contexts/CartContext'
import { SnackbarContext } from 'contexts/SnackbarContext'
import { syncProductsStockBySkus } from 'api/products'
import { addCartItem } from 'api/carts'

type OutOfStockItem = {
  sku: string
  requested: number
  available: number
}

type OrderDetailsProps = {
  order: Order
  onBack: () => void
}

const OrderDetails: React.FC<OrderDetailsProps> = ({ order, onBack }) => {
  const { t } = useTranslation()
  const {
    salesOrderNumber,
    poNumber,
    createdAt,
    OrderItems: orderItem = [],
    totalAmount,
    discountAmount,
    taxAmount,
    assemblyFee,
    finalAmount,
    transactionDetails
  } = order

  const { downloadInvoice } = useOrders()
  const { fetchCart } = useContext(CartContext)
  const { showMessage } = useContext(SnackbarContext)
  const navigate = useNavigate()

  const [isReordering, setIsReordering] = useState(false)
  const [outOfStockItems, setOutOfStockItems] = useState<OutOfStockItem[]>([])
  const [showOutOfStockDialog, setShowOutOfStockDialog] = useState(false)
  const [addedCount, setAddedCount] = useState(0)

  const handleReorder = async () => {
    setIsReordering(true)
    const outOfStock: OutOfStockItem[] = []
    const toAdd: typeof orderItem = []

    try {
      const products = await syncProductsStockBySkus(
        orderItem.map(item => item.sku)
      )
      const stockMap = Object.fromEntries(
        products.map(p => [p.sku, Number(p.stock) || 0])
      )
      for (const item of orderItem) {
        const stock = stockMap[item.sku] ?? 0
        if (stock >= item.quantity) {
          toAdd.push(item)
        } else {
          outOfStock.push({
            sku: item.sku,
            requested: item.quantity,
            available: stock
          })
        }
      }
    } catch {
      for (const item of orderItem) {
        outOfStock.push({
          sku: item.sku,
          requested: item.quantity,
          available: 0
        })
      }
    }

    for (const item of toAdd) {
      await addCartItem({
        productId: item.productId,
        quantity: item.quantity,
        isAssembled: item.isAssembled,
        hingeDirection: item.hingeDirection || null
      })
    }

    if (toAdd.length > 0) {
      await fetchCart()
    }

    setIsReordering(false)
    setAddedCount(toAdd.length)

    if (outOfStock.length > 0) {
      setOutOfStockItems(outOfStock)
      setShowOutOfStockDialog(true)
    } else if (toAdd.length > 0) {
      showMessage(t('snackbar.cart.reorderSuccess'))
      navigate('/cart')
    }
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 4 }}>
      <Box sx={{ flex: 2, mr: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
          }}
        >
          <Typography variant='h5' sx={{ fontWeight: 700, mb: 1 }}>
            {t('myAccount.orderDetails.order')} #{salesOrderNumber}
          </Typography>
          <Typography
            variant='body2'
            color='text.primary'
            sx={{ fontWeight: 600, mb: 0.5 }}
          >
            PO#: {poNumber || 'N/A'}
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            {formatDisplayDate(createdAt)}
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Grid container spacing={2}>
            {orderItem.length > 0 &&
              orderItem.map((item, index) => (
                <React.Fragment key={item.productId}>
                  <Grid item xs={12}>
                    <Grid container alignItems='center'>
                      <Grid item xs={2}>
                        <Box
                          sx={{
                            width: '100px',
                            height: '100px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                          }}
                        >
                          <Box
                            component='img'
                            src={`${PRODUCT_IMAGE_BASE_URL}${item.imageIndex}.png`}
                            alt={item.sku}
                            sx={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain'
                            }}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography
                          variant='body1'
                          sx={{
                            textTransform: 'capitalize'
                          }}
                        >
                          {' '}
                          {item.sku} <br /> {item.color}
                          <br />
                          {item.isAssembled &&
                            (item.hingeDirection
                              ? `${t(
                                  'myAccount.orderDetails.assembled'
                                )}, ${formatToTitleCase(item.hingeDirection)}`
                              : t('myAccount.orderDetails.assembled'))}
                        </Typography>
                        <Typography
                          variant='body1'
                          sx={{
                            fontWeight: 600
                          }}
                        >
                          ${Number(item.unitPrice).toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={3.5}>
                        <Typography
                          variant='body1'
                          sx={{
                            fontSize: '20px',
                            fontWeight: 600
                          }}
                        >
                          *{item.quantity}
                        </Typography>
                      </Grid>
                      <Grid item xs={1.5}>
                        <Typography
                          variant='body1'
                          sx={{
                            fontSize: '20px',
                            fontWeight: 600,
                            textAlign: 'right'
                          }}
                        >
                          ${(Number(item.unitPrice) * item.quantity).toFixed(2)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  {index < orderItem.length - 1 && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                    </Grid>
                  )}
                </React.Fragment>
              ))}
          </Grid>
        </Paper>
        <Box sx={{ display: 'flex', gap: 2, mt: 4, flexWrap: 'wrap' }}>
          <Button
            variant='outlined'
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{
              flex: 1,
              minWidth: 120,
              borderRadius: '6px',
              px: 2,
              py: 1,
              textTransform: 'none',
              fontWeight: 400
            }}
          >
            {t('myAccount.orderDetails.goBack')}
          </Button>

          <Button
            variant='contained'
            startIcon={
              isReordering ? (
                <CircularProgress size={16} color='inherit' />
              ) : (
                <ReplayIcon />
              )
            }
            onClick={handleReorder}
            disabled={isReordering}
            sx={{
              flex: 1,
              minWidth: 120,
              borderRadius: '6px',
              px: 2,
              py: 1,
              textTransform: 'none',
              fontWeight: 400
            }}
          >
            {t('myAccount.orderDetails.reorder')}
          </Button>

          {order.status !== 'CLOSED' &&
            order.status !== 'AWAITING_PAYMENT' &&
            order.status !== 'PAYMENT_PENDING' && (
              <Button
                variant='contained'
                startIcon={<DownloadIcon />}
                onClick={() =>
                  downloadInvoice(order.id, order.salesOrderNumber)
                }
                sx={{
                  flex: 1,
                  minWidth: 120,
                  borderRadius: '6px',
                  px: 2,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 400
                }}
              >
                {t('myAccount.orderDetails.downloadInvoice')}
              </Button>
            )}
        </Box>
      </Box>

      <Box sx={{ flex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CreditCardIcon sx={{ color: 'primary.main', fontSize: 22 }} />
            <Typography variant='h6' sx={{ fontWeight: 700 }}>
              {t('myAccount.orderDetails.payment')}
            </Typography>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant='body1'>
              {t('myAccount.orderDetails.creditCardType')}
            </Typography>
            <Typography variant='body1'>
              {transactionDetails?.cardType}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant='body1'>
              {t('myAccount.orderDetails.creditCardNo')}
            </Typography>
            <Typography variant='body1'>
              {transactionDetails?.cardNumber}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PersonIcon sx={{ color: 'primary.main', fontSize: 22 }} />
            <Typography variant='h6' sx={{ fontWeight: 700 }}>
              {t('myAccount.orderDetails.billingAddress')}
            </Typography>
          </Box>
          <Divider sx={{ my: 2 }} />
          {transactionDetails?.billingAddress?.firstName &&
            transactionDetails?.billingAddress?.lastName && (
              <Typography variant='body1'>
                {`${transactionDetails.billingAddress.firstName} ${transactionDetails.billingAddress.lastName}`}
              </Typography>
            )}

          {transactionDetails?.billingAddress?.address && (
            <Typography variant='body1'>
              {transactionDetails.billingAddress.address}
            </Typography>
          )}

          {(transactionDetails?.billingAddress?.city ||
            transactionDetails?.billingAddress?.state ||
            transactionDetails?.billingAddress?.zipCode ||
            transactionDetails?.billingAddress?.country) && (
            <Typography variant='body1'>
              {`${transactionDetails.billingAddress.city || ''}${
                transactionDetails.billingAddress.state
                  ? `, ${transactionDetails.billingAddress.state}`
                  : ''
              }${
                transactionDetails.billingAddress.zipCode
                  ? ` ${transactionDetails.billingAddress.zipCode}`
                  : ''
              }${
                transactionDetails.billingAddress.country
                  ? `, ${transactionDetails.billingAddress.country}`
                  : ''
              }`}
            </Typography>
          )}
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ReceiptIcon sx={{ color: 'primary.main', fontSize: 22 }} />
            <Typography variant='h6' sx={{ fontWeight: 700 }}>
              {t('myAccount.orderDetails.summary')}
            </Typography>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant='body1'>
              {t('myAccount.orderDetails.subtotal')}
            </Typography>
            <Typography variant='body1'>${totalAmount.toFixed(2)}</Typography>
          </Box>

          {discountAmount > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Typography variant='body1'>
                {t('myAccount.orderDetails.discount')}
              </Typography>
              <Typography variant='body1' sx={{ color: 'green' }}>
                -${discountAmount.toFixed(2)}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant='body1'>
              {t('myAccount.orderDetails.tax')}
            </Typography>
            <Typography variant='body1'>${taxAmount.toFixed(2)}</Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant='body1'>
              {t('myAccount.orderDetails.assemblyFee')}
            </Typography>
            <Typography variant='body1'>${assemblyFee.toFixed(2)}</Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant='h6' sx={{ fontWeight: 700 }}>
              {t('myAccount.orderDetails.total')}
            </Typography>
            <Typography variant='h6' sx={{ fontWeight: 700 }}>
              ${finalAmount.toFixed(2)}
            </Typography>
          </Box>
        </Paper>
      </Box>

      <Dialog
        open={showOutOfStockDialog}
        onClose={() => {
          setShowOutOfStockDialog(false)
          if (addedCount > 0) {
            showMessage(t('snackbar.cart.reorderSuccess'))
            navigate('/cart')
          }
        }}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>{t('myAccount.orderDetails.outOfStockTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('myAccount.orderDetails.outOfStockMessage')}
          </DialogContentText>
          {outOfStockItems.map(item => (
            <Box
              key={item.sku}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography variant='body2' sx={{ fontWeight: 600 }}>
                {item.sku}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {t('myAccount.orderDetails.available')}: {item.available}
                {' / '}
                {t('myAccount.orderDetails.requested')}: {item.requested}
              </Typography>
            </Box>
          ))}
          {addedCount > 0 && (
            <Typography variant='body2' sx={{ mt: 2, color: 'success.main' }}>
              {t('myAccount.orderDetails.outOfStockNote')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowOutOfStockDialog(false)
              if (addedCount > 0) {
                showMessage(t('snackbar.cart.reorderSuccess'))
                navigate('/cart')
              }
            }}
          >
            {t('myAccount.orderDetails.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default OrderDetails
